import * as A from 'fp-ts/Array'
import * as O from 'fp-ts/Option'
import { pipe } from 'fp-ts/function'
import { getFieldsToReset, listToMapName, mapFields, retriveAllTags } from './FormSlave'
import type { FormWorker } from './FormWorker'
import type { UseType } from './formBuilder'

export const evaluateFieldsToReset = (
    resetOnChange: FormWorker.Props['resetOnChange'],
    fields: FormWorker.InternalValueFields,
    name: string
): FormWorker.InternalValueFields =>
    Object.values(fields)
        .filter(f => getFieldsToReset(resetOnChange, name).includes(f.name))
        .map(f => ({ ...f, data: undefined, value: undefined, initialValue: undefined }))
        .reduce(listToMapName, fields)

export const findFieldAppears = (fields: FormWorker.InternalValueFields, nFields: FormWorker.InternalValueFields) => {
    const prevFieldNameNotYep = Object.values(fields)
        .filter(f => f.use !== 'yep')
        .map(f => f.name)

    return Object.values(nFields)
        .filter(f => f.use === 'yep')
        .filter(f => prevFieldNameNotYep.includes(f.name))
        .map(f => f.name)
}

export const evaluateFieldsToExlude = (
    fields: FormWorker.InternalValueFields,
    fieldsUseMarker: FormWorker.Props['fieldsUseMarker'],
    tagsUseMarker: FormWorker.Props['tagsUseMarker'],
    employeesStatus: FormWorker.EmployeesStatus
): FormWorker.InternalValueFields =>
    pipe(
        O.getMonoid(A.getMonoid<FormWorker.InternalValueFields>()).concat(
            pipe(
                O.fromNullable(tagsUseMarker),
                O.map(f =>
                    f(
                        fields,
                        retriveAllTags(fields).reduce((c, t) => ({ ...c, [t]: 'unknown' }), {}),
                        employeesStatus
                    )
                ),
                O.map(tagsToExclude =>
                    mapFields(fields, f => ({
                        ...f,
                        use:
                            Object.keys(tagsToExclude)
                                .filter(k => tagsToExclude[k] === 'none')
                                .filter(k => f.tags.includes(k)).length > 0
                                ? 'none'
                                : 'yep',
                    }))
                ),
                O.map(c => [c])
            ),
            pipe(
                O.fromNullable(fieldsUseMarker),
                O.map(fieldsUM =>
                    fieldsUM(
                        fields,
                        Object.values(fields).reduce((c, f) => ({ ...c, [f.name]: 'unknown' }), {}),
                        employeesStatus
                    )
                ),
                O.map(fieldsToExlude =>
                    mapFields(fields, f => ({
                        ...f,
                        use: Object.keys(fieldsToExlude)
                            .filter(k => fieldsToExlude[k] === 'none')
                            .includes(f.name)
                            ? 'none'
                            : 'yep',
                    }))
                ),
                O.map(c => [c])
            )
        ),
        O.map((ff: FormWorker.InternalValueFields[]) =>
            Object.keys(ff[0]).reduce(
                (c, k) => ({
                    ...c,
                    [k]: {
                        ...ff[0][k],
                        use: ff.reduce((use: UseType, fs): UseType => (fs[k].use === 'none' ? 'none' : use), 'yep'),
                    },
                }),
                {}
            )
        ),
        O.getOrElse(() => mapFields(fields, f => ({ ...f, use: 'yep' as UseType })))
    )
